import { inject, Injectable, OnDestroy, DOCUMENT } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NzMessageRef } from 'ng-zorro-antd/message';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type StackedMessageType = 'error' | 'success' | 'info' | 'warning' | 'loading';

export interface StackedMessageOptions {
  duration?: number;
  title?: string;
  titleKey?: string;
  titleTranslateParams?: Record<string, unknown>;
  skipTranslation?: boolean;
  showCloseButton?: boolean;
  closeButtonLabel?: string;
  closeButtonLabelKey?: string;
  closeButtonTranslateParams?: Record<string, unknown>;
}

export interface StackedMessageConfig extends StackedMessageOptions {
  type: StackedMessageType;
  message: string;
  translateParams?: Record<string, unknown>;
}

export interface TypedStackedMessageConfig extends StackedMessageOptions {
  message: string;
  translateParams?: Record<string, unknown>;
}

export interface StackedMessage {
  id: string;
  type: StackedMessageType;
  title: string | null;
  message: string;
}

interface RenderedMessage {
  element: HTMLElement;
  messageId: string;
  onClose: Subject<boolean>;
  remainingDuration: number;
  startedAt: number | null;
  timeoutId: number | null;
  isLeaving: boolean;
  removalTimeoutId: number | null;
}

@Injectable({ providedIn: 'root' })
export class StackedMessagesService implements OnDestroy {
  private readonly documentRef = inject(DOCUMENT);
  private readonly translateService = inject(TranslateService);

  private static readonly ACTIVE_BODY_CLASS = 'bridge-stacked-messages-active';
  private static readonly ANIMATION_DURATION = 200;
  private static readonly CLOSE_BUTTON_CLASS = 'bridge-stacked-message__close';
  private static readonly FLIP_CLASS = 'bridge-stacked-message-notice--reflow';
  private static readonly HOST_CLASS = 'bridge-stacked-messages-host';
  private static readonly HOST_NOTICE_CLASS = 'bridge-stacked-message-notice';
  private static readonly HOST_NOTICE_CONTENT_CLASS = 'bridge-stacked-message-notice-content';
  private static readonly HOST_CUSTOM_CONTENT_CLASS = 'bridge-stacked-message-custom-content';
  private static readonly LEAVE_CLASS = 'bridge-stacked-message-notice--leave';
  private static readonly MESSAGE_ROOT_CLASS = 'bridge-stacked-message';
  private static readonly ENTER_CLASS = 'bridge-stacked-message-notice--enter';
  private static readonly TYPE_CLASS_BY_MESSAGE_TYPE: Record<StackedMessageType, string> = {
    error: 'bridge-error',
    success: 'bridge-success',
    info: 'bridge-info',
    warning: 'bridge-warning',
    loading: 'bridge-loading',
  };

  private static readonly DEFAULT_DURATION_BY_TYPE: Record<StackedMessageType, number> = {
    error: 6000,
    success: 4000,
    info: 4000,
    warning: 6000,
    loading: 0,
  };
  private static readonly READING_WORDS_PER_MINUTE = 220;
  private static readonly READING_BUFFER_DURATION = 1200;
  private static readonly MAX_AUTO_DURATION = 15000;

  private readonly activeMessageIds = new Set<string>();
  private readonly destroy$ = new Subject<void>();
  private readonly messageIdMap = new Map<string, string>();
  private readonly renderedMessages = new Map<string, RenderedMessage>();
  private hostElement: HTMLElement | null = null;
  private sequence = 0;

  constructor() {
    this.registerCloseButtonListener();
  }

  public ngOnDestroy(): void {
    Array.from(this.renderedMessages.keys()).forEach(messageId => {
      this.finalizeRenderedMessage(messageId, false);
    });
    this.destroy$.next();
    this.destroy$.complete();
  }

  public show(config: StackedMessageConfig): NzMessageRef {
    const localMessageId = this.generateId();
    const message = this.resolveText(config.message, config.translateParams, config.skipTranslation);
    const title = config.titleKey
      ? this.translateService.instant(config.titleKey, config.titleTranslateParams)
      : (config.title ?? null);
    const duration = this.resolveDuration(config, title, message);
    const customType = StackedMessagesService.TYPE_CLASS_BY_MESSAGE_TYPE[config.type];
    const closeButtonLabel = this.resolveCloseButtonLabel(config);
    const content = this.buildMarkup({
      id: localMessageId,
      type: config.type,
      title,
      message,
      // showing close button by default
      showCloseButton: config.showCloseButton === false ? false : true,
      closeButtonLabel,
    });
    const element = this.renderMessageElement(localMessageId, customType, content);
    const onClose = new Subject<boolean>();

    this.trackMessage(localMessageId, {
      element,
      messageId: localMessageId,
      onClose,
      remainingDuration: duration,
      startedAt: null,
      timeoutId: null,
      isLeaving: false,
      removalTimeoutId: null,
    });

    return {
      messageId: localMessageId,
      onClose,
    };
  }

  public showError(config: TypedStackedMessageConfig): NzMessageRef {
    return this.show({ ...config, type: 'error' });
  }

  public showSuccess(config: TypedStackedMessageConfig): NzMessageRef {
    return this.show({ ...config, type: 'success' });
  }

  public showInfo(config: TypedStackedMessageConfig): NzMessageRef {
    return this.show({ ...config, type: 'info' });
  }

  public showWarning(config: TypedStackedMessageConfig): NzMessageRef {
    return this.show({ ...config, type: 'warning' });
  }

  public showLoading(config: TypedStackedMessageConfig): NzMessageRef {
    return this.show({
      ...config,
      type: 'loading',
      duration: config.duration ?? 0,
      showCloseButton: config.showCloseButton ?? false,
    });
  }

  public showWarn(config: TypedStackedMessageConfig): NzMessageRef {
    return this.showWarning(config);
  }

  public close(id?: string): void {
    if (id) {
      this.removeRenderedMessage(id, false);
      return;
    }

    const lastMessageId = Array.from(this.activeMessageIds).pop();
    if (lastMessageId) {
      this.removeRenderedMessage(lastMessageId, false);
    }
  }

  public closeAll(): void {
    Array.from(this.renderedMessages.keys()).forEach(messageId => {
      this.removeRenderedMessage(messageId, false);
    });
  }

  private resolveText(value: string, translateParams?: Record<string, unknown>, skipTranslation = false): string {
    if (skipTranslation) {
      return value;
    }

    return this.translateService.instant(value, translateParams);
  }

  private resolveDuration(config: StackedMessageConfig, title: string | null, message: string): number {
    if (config.duration !== undefined) {
      return config.duration;
    }

    const baseDuration = StackedMessagesService.DEFAULT_DURATION_BY_TYPE[config.type];
    const estimatedReadingDuration = this.getEstimatedReadingDuration(title, message);

    return Math.min(StackedMessagesService.MAX_AUTO_DURATION, Math.max(baseDuration, estimatedReadingDuration));
  }

  private getEstimatedReadingDuration(title: string | null, message: string): number {
    const readableText = this.getReadableText(title, message);

    if (!readableText) {
      return 0;
    }

    const wordCount = readableText.split(/\s+/).length;
    const millisecondsPerWord = 60000 / StackedMessagesService.READING_WORDS_PER_MINUTE;

    return Math.ceil(wordCount * millisecondsPerWord + StackedMessagesService.READING_BUFFER_DURATION);
  }

  private getReadableText(title: string | null, message: string): string {
    return [title ?? '', message]
      .map(value =>
        value
          .replace(/<[^>]*>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
      )
      .filter(Boolean)
      .join(' ');
  }

  private buildMarkup(
    message: Omit<StackedMessage, 'id'> & {
      id: string;
      showCloseButton: boolean;
      closeButtonLabel: string;
    }
  ): string {
    const liveRole = message.type === 'error' || message.type === 'warning' ? 'alert' : 'status';
    const livePriority = message.type === 'error' || message.type === 'warning' ? 'assertive' : 'polite';
    const titleMarkup = message.title ? `<div class="bridge-stacked-message__title">${message.title}</div>` : '';
    const loadingIndicatorMarkup =
      message.type === 'loading' ? '<span class="bridge-stacked-message__spinner" aria-hidden="true"></span>' : '';
    const closeButtonMarkup = message.showCloseButton
      ? [
          `<button class="${StackedMessagesService.CLOSE_BUTTON_CLASS}"`,
          `data-bridge-stacked-message-id="${message.id}"`,
          `type="button"`,
          `aria-label="${message.closeButtonLabel}">`,
          '&times;',
          '</button>',
        ].join(' ')
      : '';

    return [
      `<div class="${StackedMessagesService.MESSAGE_ROOT_CLASS} bridge-stacked-message--${message.type}" data-bridge-stacked-message-id="${message.id}" role="${liveRole}" aria-live="${livePriority}" aria-atomic="true">`,
      '<div class="bridge-stacked-message__content">',
      closeButtonMarkup,
      loadingIndicatorMarkup,
      titleMarkup,
      `<div class="bridge-stacked-message__text">${message.message}</div>`,
      '</div>',
      '</div>',
    ].join('');
  }

  private trackMessage(localMessageId: string, renderedMessage: RenderedMessage): void {
    this.messageIdMap.set(localMessageId, renderedMessage.messageId);
    this.renderedMessages.set(renderedMessage.messageId, renderedMessage);
    this.activeMessageIds.add(renderedMessage.messageId);
    this.syncBodyClass();

    this.ensureHost().appendChild(renderedMessage.element);
    this.startEnterAnimation(renderedMessage.element);
    this.startAutoCloseTimer(renderedMessage);
  }

  private startEnterAnimation(element: HTMLElement): void {
    element.classList.add(StackedMessagesService.ENTER_CLASS);
    window.setTimeout(() => {
      element.classList.remove(StackedMessagesService.ENTER_CLASS);
    }, StackedMessagesService.ANIMATION_DURATION);
  }

  private startAutoCloseTimer(renderedMessage: RenderedMessage): void {
    if (renderedMessage.remainingDuration <= 0) {
      return;
    }

    renderedMessage.startedAt = Date.now();
    renderedMessage.timeoutId = window.setTimeout(() => {
      this.removeRenderedMessage(renderedMessage.messageId, false);
    }, renderedMessage.remainingDuration);
  }

  private pauseAutoCloseTimer(messageId: string): void {
    const renderedMessage = this.renderedMessages.get(messageId);
    if (!renderedMessage || renderedMessage.timeoutId === null || renderedMessage.startedAt === null) {
      return;
    }

    window.clearTimeout(renderedMessage.timeoutId);
    renderedMessage.timeoutId = null;
    renderedMessage.remainingDuration = Math.max(
      0,
      renderedMessage.remainingDuration - (Date.now() - renderedMessage.startedAt)
    );
    renderedMessage.startedAt = null;
  }

  private resumeAutoCloseTimer(messageId: string): void {
    const renderedMessage = this.renderedMessages.get(messageId);
    if (!renderedMessage || renderedMessage.timeoutId !== null || renderedMessage.remainingDuration <= 0) {
      return;
    }

    this.startAutoCloseTimer(renderedMessage);
  }

  private removeRenderedMessage(messageId: string, userAction: boolean): void {
    const renderedMessage = this.renderedMessages.get(messageId);
    if (!renderedMessage || renderedMessage.isLeaving) {
      return;
    }

    if (renderedMessage.timeoutId !== null) {
      window.clearTimeout(renderedMessage.timeoutId);
      renderedMessage.timeoutId = null;
    }

    renderedMessage.startedAt = null;
    renderedMessage.isLeaving = true;
    renderedMessage.element.classList.remove(StackedMessagesService.ENTER_CLASS);
    renderedMessage.element.classList.add(StackedMessagesService.LEAVE_CLASS);
    renderedMessage.removalTimeoutId = window.setTimeout(() => {
      this.finalizeRenderedMessage(messageId, userAction);
    }, StackedMessagesService.ANIMATION_DURATION);
  }

  private finalizeRenderedMessage(messageId: string, userAction: boolean): void {
    const renderedMessage = this.renderedMessages.get(messageId);
    if (!renderedMessage) {
      return;
    }

    const previousPositions = this.captureReflowPositions(messageId);

    if (renderedMessage.timeoutId !== null) {
      window.clearTimeout(renderedMessage.timeoutId);
    }

    if (renderedMessage.removalTimeoutId !== null) {
      window.clearTimeout(renderedMessage.removalTimeoutId);
    }

    renderedMessage.element.remove();
    renderedMessage.onClose.next(userAction);
    renderedMessage.onClose.complete();

    this.messageIdMap.delete(messageId);
    this.renderedMessages.delete(messageId);
    this.activeMessageIds.delete(messageId);
    this.syncBodyClass();

    if (!this.activeMessageIds.size) {
      this.hostElement?.remove();
      this.hostElement = null;
      return;
    }

    this.animateReflow(previousPositions);
  }

  private captureReflowPositions(excludedMessageId: string): Map<HTMLElement, number> {
    return Array.from(this.renderedMessages.values()).reduce((positions, message) => {
      if (
        message.messageId === excludedMessageId ||
        message.isLeaving ||
        !message.element.isConnected ||
        message.element.classList.contains(StackedMessagesService.ENTER_CLASS)
      ) {
        return positions;
      }

      positions.set(message.element, message.element.getBoundingClientRect().top);
      return positions;
    }, new Map<HTMLElement, number>());
  }

  private animateReflow(previousPositions: Map<HTMLElement, number>): void {
    if (!previousPositions.size) {
      return;
    }

    window.requestAnimationFrame(() => {
      previousPositions.forEach((previousTop, element) => {
        if (!element.isConnected || element.classList.contains(StackedMessagesService.LEAVE_CLASS)) {
          return;
        }

        const nextTop = element.getBoundingClientRect().top;
        const deltaY = previousTop - nextTop;

        if (!deltaY) {
          return;
        }

        element.classList.remove(StackedMessagesService.FLIP_CLASS);
        element.style.transform = `translateY(${deltaY}px)`;
        element.getBoundingClientRect();

        window.requestAnimationFrame(() => {
          if (!element.isConnected || element.classList.contains(StackedMessagesService.LEAVE_CLASS)) {
            return;
          }

          element.classList.add(StackedMessagesService.FLIP_CLASS);
          element.style.transform = '';

          window.setTimeout(() => {
            if (!element.isConnected) {
              return;
            }

            element.classList.remove(StackedMessagesService.FLIP_CLASS);
            element.style.removeProperty('transform');
          }, StackedMessagesService.ANIMATION_DURATION);
        });
      });
    });
  }

  private registerCloseButtonListener(): void {
    if (!this.documentRef?.body) {
      return;
    }

    fromEvent(this.documentRef, 'click')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: Event) => {
        const target = event.target as HTMLElement | null;
        const closeButton = target?.closest(`.${StackedMessagesService.CLOSE_BUTTON_CLASS}`) as HTMLElement | null;

        if (!closeButton) {
          return;
        }

        const localMessageId = closeButton.getAttribute('data-bridge-stacked-message-id');
        if (!localMessageId) {
          return;
        }

        const messageId = this.messageIdMap.get(localMessageId);
        if (!messageId) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.removeRenderedMessage(messageId, true);
      });
  }

  private ensureHost(): HTMLElement {
    if (this.hostElement) {
      return this.hostElement;
    }

    const hostElement = this.documentRef.createElement('div');
    hostElement.className = `ant-message ${StackedMessagesService.HOST_CLASS}`;
    this.documentRef.body.appendChild(hostElement);
    this.hostElement = hostElement;

    return hostElement;
  }

  private renderMessageElement(messageId: string, customType: string, content: string): HTMLElement {
    const noticeElement = this.documentRef.createElement('div');
    noticeElement.className = `ant-message-notice ${StackedMessagesService.HOST_NOTICE_CLASS}`;
    noticeElement.setAttribute('data-bridge-stacked-message-id', messageId);
    noticeElement.innerHTML = [
      `<div class="ant-message-notice-content ${StackedMessagesService.HOST_NOTICE_CONTENT_CLASS}">`,
      `<div class="ant-message-custom-content ant-message-${customType} ${StackedMessagesService.HOST_CUSTOM_CONTENT_CLASS}">`,
      content,
      '</div>',
      '</div>',
    ].join('');

    noticeElement.addEventListener('mouseenter', () => {
      this.pauseAutoCloseTimer(messageId);
    });

    noticeElement.addEventListener('mouseleave', () => {
      this.resumeAutoCloseTimer(messageId);
    });

    noticeElement.addEventListener('focusin', () => {
      this.pauseAutoCloseTimer(messageId);
    });

    noticeElement.addEventListener('focusout', (event: FocusEvent) => {
      const nextTarget = event.relatedTarget as Node | null;

      if (nextTarget && noticeElement.contains(nextTarget)) {
        return;
      }

      this.resumeAutoCloseTimer(messageId);
    });

    return noticeElement;
  }

  private resolveCloseButtonLabel(config: StackedMessageConfig): string {
    if (config.closeButtonLabelKey) {
      return this.translateService.instant(config.closeButtonLabelKey, config.closeButtonTranslateParams);
    }

    if (config.closeButtonLabel) {
      return config.closeButtonLabel;
    }

    return 'Close';
  }

  private generateId(): string {
    this.sequence += 1;
    return `bridge-stacked-message-${this.sequence}`;
  }

  private syncBodyClass(): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.body.classList.toggle(StackedMessagesService.ACTIVE_BODY_CLASS, this.activeMessageIds.size > 0);
  }
}
